import { createBrowserRouter, createMemoryRouter, Navigate, RouterProvider } from 'react-router-dom';
import { PERMISSIONS } from 'auth/permissions';
import { ForbiddenPage } from 'auth/ForbiddenPage';
import { LoginPage } from 'auth/LoginPage';
import { NotFoundPage } from 'auth/NotFoundPage';
import { GuestRoute, PermissionRoute, ProtectedRoute } from 'core/routing/guards';
import { paths } from 'core/routing/paths';
import { ErrorBoundary } from 'core/errors/ErrorBoundary';
import { DashboardLayout } from 'layout/DashboardLayout';
import { OverviewPage } from 'features/assessment-dashboard/OverviewPage';
import { PatternsPage } from 'features/professional-patterns/PatternsPage';
import { PatternFormPage } from 'features/professional-patterns/PatternFormPage';
import { BuilderPage } from 'features/assessment-builder/BuilderPage';
import { QuestionEditorPage } from 'features/assessment-builder/QuestionEditorPage';
import { WeightMatrixPage, WeightsLandingPage } from 'features/weight-matrix/WeightMatrixPage';
import { AnswerDetailPage } from 'features/weight-matrix/AnswerDetailPage';
import { VersionDetailPage, VersionsPage } from 'features/assessment-versions/VersionsPage';
import { TopThreeProfileDetailPage, TopThreeProfilesPage } from 'features/top-three-profiles/TopThreeProfilesPage';
import { ResultContentPage } from 'features/result-content/ResultContentPage';
import { AttemptDetailPage, AttemptsPage } from 'features/user-attempts/AttemptsPage';
import { SpecialistsPage } from 'features/career-specialists/SpecialistsPage';
import { SpecialistFormPage } from 'features/career-specialists/SpecialistFormPage';
import { ConsultationsPage } from 'features/career-consultations/ConsultationsPage';
import { ConsultationDetailPage } from 'features/career-consultations/ConsultationDetailPage';
import { SupportPage } from 'features/support/SupportPage';
import { TicketDetailPage } from 'features/support/TicketDetailPage';
import { LegalDocumentsPage } from 'features/legal-documents/LegalDocumentsPage';
import { LegalDocumentFormPage } from 'features/legal-documents/LegalDocumentFormPage';
import { NotificationsPage } from 'features/notifications/NotificationsPage';
import { PlanDetailPage, PlanFormPage, PlansPage } from 'features/company-plans/PlansPage';
import { CompaniesPage, CompanyDetailPage } from 'features/company-plans/CompaniesPage';
import { PurchaseDetailPage, PurchasesPage } from 'features/company-plans/PurchasesPage';
import { EntitlementDetailPage, EntitlementsPage } from 'features/company-plans/EntitlementsPage';
import {
  AccessCodeDetailPage,
  AccessCodesPage,
  CodeBatchDetailPage,
  CodeBatchesPage,
} from 'features/company-plans/AccessCodesPage';
import { SponsoredAssessmentsPage, SponsoredResultPage } from 'features/company-plans/SponsoredAssessmentsPage';

function guarded(permission, element) {
  return <PermissionRoute permission={permission}>{element}</PermissionRoute>;
}

export const appRouteObjects = [
  {
    path: paths.login,
    element: <GuestRoute><LoginPage /></GuestRoute>,
  },
  { path: paths.forbidden, element: <ForbiddenPage /> },
  {
    element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
    children: [
      { path: paths.overview, element: guarded(PERMISSIONS.view, <OverviewPage />) },
      { path: paths.patterns, element: guarded(PERMISSIONS.view, <PatternsPage />) },
      { path: paths.patternCreate, element: guarded(PERMISSIONS.patternsManage, <PatternFormPage />) },
      { path: '/patterns/:id/edit', element: guarded(PERMISSIONS.patternsManage, <PatternFormPage />) },
      { path: paths.weights, element: guarded(PERMISSIONS.view, <WeightsLandingPage />) },
      { path: '/patterns/:id/weights', element: guarded(PERMISSIONS.view, <WeightMatrixPage />) },
      { path: paths.builder, element: guarded(PERMISSIONS.view, <BuilderPage />) },
      { path: paths.questionCreate, element: guarded(PERMISSIONS.questionsManage, <QuestionEditorPage />) },
      { path: '/builder/questions/:id/edit', element: guarded(PERMISSIONS.questionsManage, <QuestionEditorPage />) },
      { path: '/answers/:id', element: guarded(PERMISSIONS.view, <AnswerDetailPage />) },
      { path: paths.versions, element: guarded(PERMISSIONS.view, <VersionsPage />) },
      { path: '/versions/:id', element: guarded(PERMISSIONS.view, <VersionDetailPage />) },
      { path: paths.topThreeProfiles, element: guarded(PERMISSIONS.topThreeProfilesView, <TopThreeProfilesPage />) },
      { path: '/top-three-profiles/:id', element: guarded(PERMISSIONS.topThreeProfilesView, <TopThreeProfileDetailPage />) },
      {
        path: paths.resultContent,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.resultContentView, PERMISSIONS.tagsManage]}>
            <ResultContentPage />
          </PermissionRoute>
        ),
      },
      { path: paths.advices, element: <Navigate to={paths.resultContent} replace /> },
      { path: '/advices/new', element: <Navigate to={paths.resultContent} replace /> },
      { path: '/advices/:id/edit', element: <Navigate to={paths.resultContent} replace /> },
      { path: paths.attempts, element: guarded(PERMISSIONS.attemptsView, <AttemptsPage />) },
      { path: '/attempts/:id', element: guarded(PERMISSIONS.attemptsView, <AttemptDetailPage />) },
      {
        path: paths.specialists,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.specialistsView, PERMISSIONS.specialistsManage]}>
            <SpecialistsPage />
          </PermissionRoute>
        ),
      },
      { path: paths.specialistCreate, element: guarded(PERMISSIONS.specialistsManage, <SpecialistFormPage />) },
      { path: '/specialists/:id/edit', element: guarded(PERMISSIONS.specialistsManage, <SpecialistFormPage />) },
      {
        path: '/specialists/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.specialistsView, PERMISSIONS.specialistsManage]}>
            <SpecialistFormPage readOnly />
          </PermissionRoute>
        ),
      },
      {
        path: paths.consultations,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.consultationsView, PERMISSIONS.consultationsManage]}>
            <ConsultationsPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/consultations/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.consultationsView, PERMISSIONS.consultationsManage]}>
            <ConsultationDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.support,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.supportView, PERMISSIONS.supportManage]}>
            <SupportPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/support/tickets/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.supportView, PERMISSIONS.supportManage]}>
            <TicketDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.legalDocuments,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.legalDocumentsView, PERMISSIONS.legalDocumentsManage]}>
            <LegalDocumentsPage />
          </PermissionRoute>
        ),
      },
      { path: paths.legalDocumentCreate, element: guarded(PERMISSIONS.legalDocumentsManage, <LegalDocumentFormPage />) },
      { path: '/legal-documents/:id/edit', element: guarded(PERMISSIONS.legalDocumentsManage, <LegalDocumentFormPage />) },
      {
        path: '/legal-documents/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.legalDocumentsView, PERMISSIONS.legalDocumentsManage]}>
            <LegalDocumentFormPage readOnly />
          </PermissionRoute>
        ),
      },
      {
        path: paths.notifications,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.notificationsView, PERMISSIONS.notificationsManage]}>
            <NotificationsPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.companyPlans,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.plansView, PERMISSIONS.plansManage]}>
            <PlansPage />
          </PermissionRoute>
        ),
      },
      { path: paths.companyPlanCreate, element: guarded(PERMISSIONS.plansManage, <PlanFormPage />) },
      { path: '/company-plans/:id/edit', element: guarded(PERMISSIONS.plansManage, <PlanFormPage />) },
      {
        path: '/company-plans/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.plansView, PERMISSIONS.plansManage]}>
            <PlanDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.companies,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.companiesView, PERMISSIONS.companiesManage]}>
            <CompaniesPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/companies/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.companiesView, PERMISSIONS.companiesManage]}>
            <CompanyDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.planPurchases,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.purchasesView, PERMISSIONS.purchasesManage]}>
            <PurchasesPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/plan-purchases/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.purchasesView, PERMISSIONS.purchasesManage]}>
            <PurchaseDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.companyEntitlements,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.entitlementsView, PERMISSIONS.entitlementsManage]}>
            <EntitlementsPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/company-entitlements/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.entitlementsView, PERMISSIONS.entitlementsManage]}>
            <EntitlementDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.companyCodeBatches,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.codesView, PERMISSIONS.codesManage]}>
            <CodeBatchesPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/company-code-batches/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.codesView, PERMISSIONS.codesManage]}>
            <CodeBatchDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.companyAccessCodes,
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.codesView, PERMISSIONS.codesManage]}>
            <AccessCodesPage />
          </PermissionRoute>
        ),
      },
      {
        path: '/company-access-codes/:id',
        element: (
          <PermissionRoute anyOf={[PERMISSIONS.codesView, PERMISSIONS.codesManage]}>
            <AccessCodeDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: paths.sponsoredAssessments,
        element: guarded(PERMISSIONS.sponsoredResultsView, <SponsoredAssessmentsPage />),
      },
      {
        path: '/sponsored-assessments/:attemptId/result',
        element: guarded(PERMISSIONS.sponsoredResultsView, <SponsoredResultPage />),
      },
    ],
  },
  { path: '/', element: <Navigate to={paths.overview} replace /> },
  { path: '*', element: <NotFoundPage /> },
];

const routerOptions = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
};

export function createTestRouter(route = '/login') {
  return createMemoryRouter(appRouteObjects, { initialEntries: [route] });
}

const browserRouter = createBrowserRouter(appRouteObjects, routerOptions);

export default function App({ router = browserRouter }) {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
